import { ArticleModelLifeCycleCode, type ArticleGroupPlacement } from 'api/models';
import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import { mapPlacementToArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const isVector = (value: unknown): boolean =>
    isRecord(value) && ['x', 'y', 'z'].every((axis) => typeof value[axis] === 'number' && Number.isFinite(value[axis]));

export function getValidatedPlacements(result: unknown): ArticleGroupPlacement[] {
    if (!isRecord(result) || !Array.isArray(result.placements) || result.placements.length > 10000) {
        throw new Error('The proposal does not contain valid placements.');
    }
    const ids = new Set<string>();
    for (const group of result.placements) {
        if (
            !isRecord(group) ||
            typeof group.articleGuid !== 'string' ||
            !group.articleGuid ||
            typeof group.articleNumber !== 'string' ||
            !Array.isArray(group.instances)
        ) {
            throw new Error('Invalid article placement group.');
        }
        for (const instance of group.instances) {
            if (
                !isRecord(instance) ||
                typeof instance.id !== 'string' ||
                !instance.id ||
                ids.has(instance.id) ||
                !Array.isArray(instance.poiGuids) ||
                instance.poiGuids.length === 0 ||
                !instance.poiGuids.every((id) => typeof id === 'string') ||
                !Array.isArray(instance.parts) ||
                instance.parts.length === 0 ||
                !instance.parts.every(
                    (part) =>
                        isRecord(part) &&
                        typeof part.name === 'string' &&
                        isVector(part.position) &&
                        isVector(part.alignment) &&
                        isVector(part.scale) &&
                        typeof part.rotation === 'number' &&
                        Number.isFinite(part.rotation)
                )
            ) {
                throw new Error('Invalid or duplicate article placement instance.');
            }
            ids.add(instance.id);
        }
    }
    // The runtime shape is validated before mapping to the generated API model.
    return result.placements as ArticleGroupPlacement[];
}

export function applyPlacementGroups(result: unknown): void {
    const placements = getValidatedPlacements(result);
    const state = usePlacementStore.getState();
    if (state.isViewMode || state.isDraggingActive() || state.isTransformationActiveRef.current) {
        throw new Error('Finish the active interaction and enable editing before applying placements.');
    }
    const poiIds = new Set(state.pois.map(({ poi }) => poi.poiGuid));
    const existingIds = new Set(
        Object.values(state.articles).flatMap((article) => Object.keys(article.articleInstances))
    );
    if (
        placements.some((group) =>
            group.instances.some(
                (instance) => existingIds.has(instance.id) || instance.poiGuids.some((id) => !poiIds.has(id))
            )
        )
    ) {
        throw new Error('The proposal conflicts with the current workspace. Request a new proposal.');
    }
    const instances = placements.flatMap((group) =>
        group.instances.map((instance) => ({
            articleId: group.articleGuid,
            instanceId: instance.id,
            instance: mapPlacementToArticleInstance(instance)
        }))
    );

    state.runHistoryTransaction('historyPlace', () => {
        state.batchAddArticles(
            placements.map((group) => ({
                id: group.articleGuid,
                articleNumber: group.articleNumber,
                lifeCycleCode: group.lifeCycleCode ?? ArticleModelLifeCycleCode.NA
            }))
        );
        state.updateArticleInstances(instances, true);
        usePlacementStore.setState({
            placementUpdate: {
                event: PlacementUpdateEvent.PLACE,
                articleInstanceIds: new Set(instances.map(({ instanceId }) => instanceId))
            }
        });
    });
}
